import { computed, Injectable, signal } from '@angular/core';

import { IUserRole, IUserPermission, IUserBasic, UserScope } from '@core/interfaces';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  roles$ = signal<IUserRole[] | null>(null);
  permissions$ = signal<IUserPermission>({});
  user$ = signal<IUserBasic | null>(null);
  private availableUserScopes$ = signal<UserScope[]>([]);

  availableUserScopes = this.availableUserScopes$.asReadonly();

  canReadAllUsers = computed(() => {
    return this.roles$()?.some((role) => role.can_read_all_users);
  });

  /**
   * Synchronous permission check against the loaded permission set. Matches an
   * exact action or its `:all` variant (e.g. `delete` matches `delete` or
   * `delete:all`). Returns false if permissions have not loaded yet.
   */
  hasPermissionSync(resource: string, action: string): boolean {
    const perms = this.permissions$();
    if (!perms || !perms[resource]) return false;
    return perms[resource].some(
      (allowedAction) => allowedAction === action || allowedAction === `${action}:all`,
    );
  }

  setUserRolesAndPermissions(
    user: IUserBasic,
    roles: IUserRole[],
    permissions: IUserPermission,
    availableUserScopes: UserScope[] = [],
  ) {
    this.user$.set(user);
    this.roles$.set(roles);
    this.permissions$.set(permissions);
    this.availableUserScopes$.set(availableUserScopes);
  }

  async hasPermission(resource: string, action: string, createdBy: string | null) {
    if (this.permissions$() === null || this.roles$() === null) {
      return;
    }

    if (!this.permissions$()[resource]) {
      return false;
    }

    let permission = false;

    // check for all permissions
    permission = this.permissions$()[resource].some(
      (allowedAction) => `${action}:all` === allowedAction || action === allowedAction,
    );

    if (permission) return true;

    // check for own permissions
    if (createdBy && createdBy === this.user$()?.id) {
      permission = this.permissions$()[resource].some(
        (allowedAction) => `${action}:own` === allowedAction,
      );
    }

    return permission;
  }
}
